import React, { useEffect, useState, useMemo } from "react";
import { getMonthName } from "../../services/appService";
import geo from "../../services/geoService";
import useForm from "../../app/hooks/useForm";
import useControls from "../../app/hooks/useControls";

const ViewCandidate = ({
  viewShowHide,
  handleViewShowHide,
  viewType,
  selectedObj,
  handleDurationShowHide,
  filterSubcontractorList,
  filterRecruiterList,
}) => {
  const [stateList, setStateList] = useState([]);
  const [subContractorList, setSubContractorList] = useState([]);
  const [recruiterList, setRecruiterList] = useState([]);
  const [aWorkfinityAppEntity, setAWorkfinityAppEntity] = useState({});
  const [entityName, setEntityName] = useState("");

  const candidateEntityOptions = [
    { id: 1, name: "C2C Basis" },
    { id: 2, name: "W2 Basis" },
    { id: 3, name: "1099 Basis" },
  ];

  const mspModes = [
    { id: 1, name: "Direct" },
    { id: 2, name: "MSP-DotStaff" },
    { id: 3, name: "MSP-ACRO" },
    { id: 4, name: "MSP-CAI" },
    { id: 5, name: "MSP-TAFFIN" },
    { id: 6, name: "Others" },
  ];

  const schema = {};
  const doSubmit = () => {};

  const {
    validate,
    handleChange,
    stateData,
    setStateData,
    stateErrors,
    setStateErrors,
    handleSubmit,
  } = useForm(schema, doSubmit);

  const { renderSelect, renderInput, renderTextArea } = useControls(
    stateData,
    stateErrors,
    handleChange,
    schema
  );

  useEffect(() => {
    setStateList(geo.getStateList());
  }, []);

  useEffect(() => {
    if (!selectedObj) return;

    const parsedData = {
      id: selectedObj.performerid,
      ipcsId: selectedObj.maxloans,
      recruiter: selectedObj.recruiter?.fuid || 0,
      subcontractor: selectedObj.validId,
      Rate: selectedObj.rates?.subcontractorRate,
      Recruiter_Rate: selectedObj.rates?.recruiterRate,
    };

    const parsedApp = JSON.parse(selectedObj.performerData);
    const appShortName = Object.keys(parsedApp)[1];
    const appEntity = parsedApp[appShortName];

    Object.keys(appEntity).forEach((key) => {
      if (["@xmlns:xsd", "@xmlns:xsi", "EntityId"].includes(key)) return;
      Object.entries(appEntity[key]).forEach(([k, v]) => {
        if (!Array.isArray(v)) {
          parsedData[k.replace("@", "")] = v;
        }
      });
    });

    setSubContractorList(filterSubcontractorList);
    setRecruiterList(filterRecruiterList);
    setEntityName(appShortName);
    setAWorkfinityAppEntity(appEntity);
    setStateData(parsedData);
    setStateErrors({});
    validate(parsedData);
  }, [selectedObj]);

  // Calculate derived fields
  useEffect(() => {
    if (
      stateData.CandidateEntity === "W2 Basis" &&
      stateData.Rate_hour_Paid_to_Candidate &&
      stateData.Tax
    ) {
      const rate = parseFloat(stateData.Rate_hour_Paid_to_Candidate);
      const tax = parseFloat(stateData.Tax);
      const finalRate = rate + (rate * tax) / 100;
      setStateData((prev) => ({ ...prev, finalRate }));
    }
  }, [stateData.CandidateEntity, stateData.Rate_hour_Paid_to_Candidate, stateData.Tax]);

  useEffect(() => {
    if (stateData.StartDate && stateData.EngagementDuration) {
      const start = new Date(stateData.StartDate);
      start.setMonth(start.getMonth() + parseInt(stateData.EngagementDuration));
      const endDate = `${start.getDate()}-${getMonthName(start.getMonth() + 1)}-${start.getFullYear()}`;
      setStateData((prev) => ({ ...prev, EndDate: endDate }));
    }
  }, [stateData.StartDate, stateData.EngagementDuration]);

  // Handle Partner Address auto-fill
  const partnerData = useMemo(() => {
    const list = viewType === "subcontractor" ? subContractorList : recruiterList;
    const id = viewType === "subcontractor" ? stateData.subcontractor : stateData.recruiter;
    return list?.find((x) => x.fuid === parseInt(id));
  }, [viewType, stateData.subcontractor, stateData.recruiter]);

  useEffect(() => {
    if (partnerData) {
      setStateData((prev) => ({
        ...prev,
        AddressLine1: partnerData.accessKey,
        AddressLine2: partnerData.portalUrl,
        City1: partnerData.city,
        State1: partnerData.state,
        Zip1: partnerData.zip,
      }));
    }
  }, [partnerData]);

  // --- Render UI based on viewType ---
  const renderSection = () => {
    switch (viewType) {
      case "agency":
        return (
          <div className="row">
            <div className="col-md-6">{renderSelect("State", "State", stateList, "name", "name")}</div>
            <div className="col-md-6">{renderInput("AgencyName", "Agency Name")}</div>
            <div className="col-md-6">{renderInput("PositionTitle", "Position Title")}</div>
            <div className="col-md-6">{renderInput("PositionID", "Position ID")}</div>
            <div className="col-md-12">{renderInput("PositionLocation", "Position Location")}</div>
            <div className="col-md-3">{renderInput("StartDate", "Start Date", "date")}</div>
            <div className="col-md-3">{renderInput("EngagementDuration", "Engagement Duration")}</div>
            <div className="col-md-1 d-flex align-items-center justify-content-center"><label htmlFor="" className="form-control-label">OR</label></div>
            <div className="col-md-2">{renderInput("Engagement_Day", "Days", "number")}</div>
            <div className="col-md-3">{renderInput("EndDate", "End Date")}</div>
            <div className="col-md-12">{renderInput("RatePayableIpcs", "Rate/Hour Received By IPCS $")}</div>
            <div className="col-md-12">{renderInput("ProjectDescription", "Project Description")}</div>
            <div className="col-md-12 form-group"><p className="form-control-label bg-admin-light border-admin-left p-2 w-100">Agency Contact 1</p></div>
            <div className="col-md-6">{renderInput("AgencyContact", "Name")}</div>
            <div className="col-md-6">{renderInput("AgencyTitle", "Title")}</div>
            <div className="col-md-6">{renderInput("AgencyEmail", "Email")}</div>
            <div className="col-md-6">{renderInput("AgencyPhone", "Phone", "phone")}</div>
            <div className="col-md-12 form-group"><p className="form-control-label bg-admin-light border-admin-left p-2 w-100">Agency Contact 2</p></div>
            <div className="col-md-6">{renderInput("AgencyContactName2", "Name")}</div>
            <div className="col-md-6">{renderInput("AgencyContactTitle2", "Title")}</div>
            <div className="col-md-6">{renderInput("AgencyContactEmail2", "Email")}</div>
            <div className="col-md-6">{renderInput("AgencyContactPhone2", "Phone No.", "phone")}</div>
            <div className="col-md-12 form-group"><p className="form-control-label bg-admin-light border-admin-left p-2 w-100">Agency Contact 3</p></div>
             <div className="col-md-6">{renderInput("AgencyContactName3", "Name")}</div>
            <div className="col-md-6">{renderInput("AgencyContactTitle3", "Title")}</div>
            <div className="col-md-6">{renderInput("AgencyContactEmail3", "Email")}</div>
            <div className="col-md-6">{renderInput("AgencyContactPhone3", "Phone No.", "phone")}</div>
          </div>
        );

      case "msp":
        return (
          <div className="row">
            <div className="col-md-12">{renderSelect("Msp_Mode", "MSP Mode", mspModes, "name", "name")}</div>
            <div className="col-md-12">{renderInput("Msp_AdminFee", "MSP Admin Fee %")}</div>
            <div className="col-md-12 form-group"><p className="form-control-label bg-admin-light border-admin-left p-2 w-100">MSP Contact 1</p></div>
            <div className="col-md-4">{renderInput("Msp_Contact1_Name", "Name")}</div>
            <div className="col-md-4">{renderInput("Msp_Contact1_Email", "Email")}</div>
            <div className="col-md-4">{renderInput("Msp_Contact1_Phone", "Phone", "phone")}</div>
            <div className="col-md-12 form-group"><p className="form-control-label bg-admin-light border-admin-left p-2 w-100">MSP Contact 2</p></div>
            <div className="col-md-4">{renderInput("Msp_Contact2_Name", "Name")}</div>
            <div className="col-md-4">{renderInput("Msp_Contact2_Email", "Email")}</div>
            <div className="col-md-4">{renderInput("Msp_Contact2_Phone", "Phone", "phone")}</div>
            <div className="col-md-12">{renderTextArea("mspNotes", "Notes")}</div>
          </div>
        );

      case "candidate":
        return (
          <div className="row">
            <div className="col-md-12">{renderInput("ipcsId", "IPCS Existing ID")}</div>
            <div className="col-lg-4">{renderInput("First_Name", "First Name")}</div>
            <div className="col-lg-4">{renderInput("Middle_Name", "Middle Name")}</div>
            <div className="col-lg-4">{renderInput("Last_Name", "Last Name")}</div>
            <div className="col-lg-6">{renderInput("Email", "Email")}</div>
            <div className="col-lg-6">{renderInput("Mobile_No", "Mobile Number", "phone")}</div>

            {stateData.CandidateEntity === "1099 Basis" && (
              <div className="col-md-12">
                {renderInput("Rate_hour_Paid_to_Candidate", "Rate/hour-Paid to Candidate $")}
              </div>
            )}

            {stateData.CandidateEntity === "W2 Basis" && (
              <>
                <div className="col-md-6">{renderInput("Rate_hour_Paid_to_Candidate", "Rate/hour-Paid to Candidate $")}</div>
                <div className="col-md-6">{renderInput("Tax", "Tax Rate %")}</div>
                <div className="col-md-6">{renderInput("finalRate", "Final Rate/hour-Paid to Candidate $")}</div>
                {stateData.w9_Type === "W2 Hourly with Benefits" && (
                  <div className="col-md-6">{renderInput("costOfBenefit", "Cost Of Benefit $")}</div>
                )}
              </>
            )}
            <div className="col-md-12">{renderTextArea("notes", "Notes")}</div>
          </div>
        );

      case "subcontractor":
        return stateData.CandidateEntity === "C2C Basis" ? (
          <div className="row">
            <div className="col-md-6">{renderSelect("subcontractor", "Select Subcontractor", subContractorList, "label", "fuid")}</div>
            <div className="col-md-6">{renderInput("Rate", "Rate/Hour Paid to Subcontractor $")}</div>
          </div>
        ) : null;

      case "recruiter":
        return (
          <div className="row">
            <div className="col-md-8">{renderSelect("recruiter", "Recruiter", recruiterList, "label", "fuid")}</div>
            <div className="col-md-4">{renderInput("Recruiter_Rate", "Rate/Hour Paid to Recruiter $")}</div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="col-md-12">
      <div className="col-md-12 text-left mb-2 d-flex justify-content-between">
        <button className="btn btn-sm btn-outline-default" onClick={() => handleDurationShowHide("", "", -1)}>
          <i className="fa fa-times mr-2"></i>Close
        </button>
        <h2 className="m-0 text-left">{viewType?.charAt(0).toUpperCase() + viewType?.slice(1)} Details</h2>
        <div />
      </div>

      <div className="col-md-12">
        <fieldset disabled={true}>
          {renderSection()}

          {(viewType === "subcontractor" || viewType === "recruiter") && partnerData && (
            <div className="row">
              <div className="col-md-6">{renderInput("AddressLine1", "Address Line 1")}</div>
              <div className="col-md-6">{renderInput("AddressLine2", "Address Line 2")}</div>
              <div className="col-md-4">{renderInput("City1", "City")}</div>
              <div className="col-md-4">{renderSelect("State1", "State", stateList, "name", "name")}</div>
              <div className="col-md-4">{renderInput("Zip1", "Zip")}</div>
            </div>
          )}
        </fieldset>
      </div>
    </div>
  );
};

export default ViewCandidate;
